import React from 'react';

import { SortableChatTabComponent, ChatTabComponent } from 'react/chat/inner/ChatTabComponent';
import { Request } from '../../components/Request';
import openAddChannelPopup from 'react/matchmaking/unified/openAddChannelPopup';
import { ChatTabComponentChatRoom } from './inner/ChatTabComponentChatRoom';

export default class ChatTabHolder extends React.Component {
	static defaultProps = {
		chats: new Map(),
		showAddNewChatButton: true,
	};

	constructor(props) {
		super(props);
		const { chats } = this.props;
		this.possibleComponents = {
			'sortable': SortableChatTabComponent,
			'static': ChatTabComponent
		};

		this.state = {
			chats: new Map(chats),
			sortableAllowed: true
		};

		this.sortItems = this.sortItems.bind(this);
		this.enableParentSort = this.enableParentSort.bind(this);
		this.disableParentSort = this.disableParentSort.bind(this);
		this.onCreateChatClick = this.createChatClick.bind(this);
	}

	enableParentSort() {
		this.setState({
			sortableAllowed: true
		});
	}

	disableParentSort() {
		this.setState({
			sortableAllowed: false
		});
	}

	getChatComponentType() {
		const { sortable } = this.props;
		const { sortableAllowed } = this.state;

		return this.possibleComponents[sortable && sortableAllowed ? 'sortable' : 'static'];
	}

	createChatClick() {
		openAddChannelPopup();
	}

	static _chatListsAreTheSame(map1, map2) {
		if(map1.size !== map2.size) {
			return false;
		}
		for(var [key, val] of map1) {
			const testVal = map2.get(key);
			// in cases of an undefined value, make sure the key
			// actually exists on the object so there are no false positives
			if(testVal !== val || (testVal === undefined && !map2.has(key))) {
				return false;
			}
		}
		return true;
	}

	componentDidUpdate(prevProps, prevState) {
		//This is so that the chat lists are drag sortable and such
		if(!ChatTabHolder._chatListsAreTheSame(prevState.chats, this.props.chats)) {
			this.setState({
				chats: new Map(this.props.chats)
			});
		}
	}

	sortItems(items) {
		const updatedChats = [];
		const updatedChatKeys = {};

		try {
			if(items.length && Array.isArray(items[0])) //Sort groups
			{
				items.forEach((chatGroup) => {
					chatGroup[1].chats.forEach((chatRoom) => {
						updatedChats.push(chatRoom);
						updatedChatKeys[chatRoom.id] = chatRoom.id;
					});
				});
			}
			else // Sort individual Chats
			{
				items.forEach((chatRoom) => {
					if(chatRoom === undefined) {
						throw new Error('Chat was undefined');
					}
					updatedChats.push(chatRoom);
					updatedChatKeys[chatRoom.id] = chatRoom.id;
				});
			}
		} catch(error) {
			console.error(error);
			return;
		}


		const pendingChatList = [];
		let insertPosition = null;
		let currentIndex = 0;
		this.state.chats.forEach((chat) => {

			if(updatedChatKeys[chat.id] && insertPosition === null) {
				// Find first insert position to merge the updated list into.  This should cause those entries to be overwritten when the list is merged
				insertPosition = currentIndex;
			}
			else {
				pendingChatList.push(chat);
			}
			currentIndex++;
		});
		pendingChatList.splice(insertPosition, 0, ...updatedChats);
		const duplicatesRemoved = new Set(pendingChatList);

		const mappedChats = new Map();
		let newOrderIndexes = 0;
		duplicatesRemoved.forEach((chat) => {
			chat.order = newOrderIndexes;
			mappedChats.set(chat.id, chat);
			newOrderIndexes++;
		});
		this.setState({
			chats: mappedChats
		});
		if(this.submitSortTimeout) {
			clearTimeout(this.submitSortTimeout);
		}
		this.submitSortTimeout = setTimeout(() => {
			const newOrder = [];
			mappedChats.forEach((chat) => {
				newOrder.push(chat.id);
			});
			Request.apiv1('chat/my_chats', { order: newOrder });
		}, 1000);
	}

	static retrieveProperChatGroup(chatGroupList, priority, groupKey, groupName, groupImage, parent) {
		let entry = chatGroupList.get(groupKey);
		if(!entry) {
			entry = {
				groupName,
				groupImage,
				priority,
				parent,
				chats: [],
				isActive: false,
			};
			chatGroupList.set(groupKey, entry);
		}
		return entry;
	};

	_convertGroupToComponents() {
		const { sortable } = this.props;
		const { chats } = this.state;
		const converted = Array.from(chats);
		converted.sort(([_, a], [__, b]) => {
			if(a.order === b.order) {
				return 0;
			}
			return a.order > b.order ? 1 : -1;
		});

		const groupedChatRooms = new Map();
		converted.forEach(([_, chatRoom]) => {
			let chatGroup = null;
			if(chatRoom.ladder) {
				chatGroup = ChatTabHolder.retrieveProperChatGroup(groupedChatRooms, 2, `ladder ${chatRoom.ladder.id}`, chatRoom.ladder.name, chatRoom.ladder.smallImageUrl(), chatRoom.ladder);
			}
			else if(chatRoom.mod_chat_only) {
				chatGroup = ChatTabHolder.retrieveProperChatGroup(groupedChatRooms, 1, 'Mod', 'Mod', null);
			}
			else {
				chatGroup = ChatTabHolder.retrieveProperChatGroup(groupedChatRooms, 2, 'Others', 'Others', chatRoom.ladder);
			}
			if(chatRoom.isActive()) {
				chatGroup.isActive = true;
			}
			if(chatRoom.chatMessages.hasNewMentions) {
				chatGroup.isActive = true;
			}
			chatGroup.chats.push(chatRoom);
		});
		const groupedChatRoomsConverted = Array.from(groupedChatRooms).sort(([_, a], [__, b]) => {
			if(a.priority !== b.priority) {
				return a.priority > b.priority ? 1 : -1;
			}
			if(a.parent && b.parent && a.parent.ladder_id && b.parent.ladder_id) {
				return a.parent.ladder_id > b.parent.ladder_id ? 1 : -1;
			}
			return 0;
		});

		const ChatTabComponentType = this.getChatComponentType();
		return groupedChatRoomsConverted.map(([name, chatGroup], sortId) => {

			const sortProps = {};
			if(this.props.sortable) {
				sortProps.onSortItems = this.sortItems;
				sortProps.items = groupedChatRoomsConverted;
				sortProps.sortId = sortId;
			}

			return (
				<ChatTabComponentType
					key={chatGroup.groupName}
					sortable={sortable}
					disableParentSort={this.disableParentSort}
					enableParentSort={this.enableParentSort}
					onChatWasSelected={this.props.onChatWasSelected}
					name={chatGroup.name}
					parent={chatGroup.parent}
					parentName={chatGroup.groupName}
					parentImage={chatGroup.groupImage}
					chatRooms={chatGroup.chats}
					isActive={chatGroup.isActive}
					hasNewMentions={chatGroup.hasNewMentions}

					{...sortProps}

				/>
			);
		});
	}

	render() {
		const { showAddNewChatButton, chats, title, sortable, displayStyle } = this.props;
		if(!sortable && (!chats || !chats.size)) {
			return null;
		}
		let chatGroups = null;
		if(displayStyle === 'horizontal') {
			const ChatTabComponentType = this.getChatComponentType();
			console.log('waht are chats', chats);
			chatGroups = Array.from(chats).map(([id, chat]) => {
				return (
					<ChatTabComponentChatRoom
						key={chat.id}
						chatRoom={chat}
					/>
				);
			});
		}
		else {
			chatGroups = this._convertGroupToComponents();
		}

		return (
			<div className='chat_tab_holder'>
				{showAddNewChatButton &&
				<div
					onClick={this.onCreateChatClick}
					className='chat_tab control_tab btn-success create_chat clickable'>
					<i className='fa fa-plus' />
				</div>
				}
				<div className='chat_tab_mover'>
					<div className='chat_tab_group'>
						{chatGroups}
					</div>
				</div>
			</div>
		);
	}
}